import type { Experiment } from 'types';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { isPreStart } from 'helpers/experiment-status';

const VIEWS = ['All', 'Draft', 'Running', 'Ended'] as const;
export type TestExperimentViews = (typeof VIEWS)[number];

export const useTestExperiments = () => {
  const apiClient = useClient();
  const queryClient = useQueryClient();
  const [view, setView] = useState<TestExperimentViews>('All');

  const { data: allTestExperiments, isLoading } = useQuery({
    queryKey: ['TEST_EXPERIMENTS'],
    queryFn: async () => apiClient.get<Experiment[]>('/experiments/test'),
  });

  const testExperiments = allTestExperiments ?? [];

  // A draft in QA is listed and counted with the drafts, as everywhere else.
  const inView = (exp: Experiment) => (view === 'Draft' ? isPreStart(exp.status) : exp.status === view);
  const filteredExperiments = view === 'All' ? testExperiments : testExperiments.filter(inView);

  const counts = {
    All: testExperiments.length,
    Draft: testExperiments.filter((exp: Experiment) => isPreStart(exp.status)).length,
    Running: testExperiments.filter((exp: Experiment) => exp.status === 'Running').length,
    Ended: testExperiments.filter((exp: Experiment) => exp.status === 'Ended').length,
  };

  const duplicateExperiment = async (experimentId: number, name: string) => {
    await apiClient.post(`/experiments/${experimentId}/duplicate?forTest=true`, { name });
    queryClient.invalidateQueries({ queryKey: ['TEST_EXPERIMENTS'] });
  };

  const deleteTestExperiment = async (experimentId: number) => {
    await apiClient.delete(`/experiments/${experimentId}/test`);
    queryClient.invalidateQueries({ queryKey: ['TEST_EXPERIMENTS'] });
  };

  const resetTestExperiment = async (experimentId: number) => {
    await apiClient.post(`/experiments/${experimentId}/test/reset`);
    queryClient.invalidateQueries({ queryKey: ['TEST_EXPERIMENTS'] });
  };

  return {
    testExperiments: filteredExperiments,
    allTestExperiments: testExperiments,
    isLoading,
    VIEWS,
    view,
    setView,
    counts,
    duplicateExperiment,
    deleteTestExperiment,
    resetTestExperiment,
  };
};
